<template>
  <Dialog
    :visible="visible"
    :modal="true"
    :draggable="false"
    :closable="true"
    :style="{ width: '560px', maxWidth: '94vw' }"
    class="segment-values-dialog"
    @update:visible="(value) => $emit('update:visible', value)"
  >
    <template #header>
      <div class="segment-values-dialog__header">
        <span class="segment-values-dialog__title">{{ title || t('segmentation.valuesField.title') }}</span>
        <span class="segment-values-dialog__count">{{ countLabel(draft.length) }}</span>
      </div>
    </template>

    <div class="segment-values-dialog__add">
      <Textarea
        v-model="addText"
        rows="2"
        :auto-resize="false"
        class="segment-values-dialog__add-input"
        :placeholder="t('segmentation.valuesField.addPlaceholder')"
        @keydown.enter.exact.prevent="addFromText"
        @input="addMessage = null"
      />
      <Button
        type="button"
        :label="t('segmentation.valuesField.add')"
        class="p-button-secondary"
        @click="addFromText"
      />
    </div>
    <small
      class="segment-values-dialog__message"
      :class="{ 'p-error': addMessage?.error }"
    >{{ addMessage ? addMessage.text : t('segmentation.valuesField.addHint') }}</small>

    <div class="segment-values-dialog__toolbar">
      <span class="p-input-icon-left segment-values-dialog__search">
        <i class="fa-regular fa-magnifying-glass" />
        <InputText
          v-model="search"
          :placeholder="t('segmentation.valuesField.search')"
        />
      </span>
      <Button
        type="button"
        icon="fa-regular fa-copy"
        :label="copied ? t('segmentation.valuesField.copied') : t('segmentation.valuesField.copy')"
        class="p-button-text p-button-secondary p-button-sm segment-values-dialog__tool"
        :disabled="!draft.length"
        @click="copy"
      />
      <Button
        type="button"
        icon="fa-regular fa-trash"
        :label="t('segmentation.valuesField.clear')"
        class="p-button-text p-button-danger p-button-sm segment-values-dialog__tool"
        :disabled="!draft.length"
        @click="clearAll"
      />
    </div>

    <div
      v-if="filteredItems.length"
      class="segment-values-dialog__list"
    >
      <VirtualScroller
        :items="filteredItems"
        :item-size="rowHeight"
        :style="{ height: listHeight }"
      >
        <template #item="{ item }">
          <div
            class="segment-values-dialog__row"
            :style="{ height: `${rowHeight}px` }"
          >
            <span
              class="segment-values-dialog__value"
              :title="item.label"
            >{{ item.label }}</span>
            <Button
              type="button"
              icon="fa-regular fa-xmark"
              class="p-button-text p-button-secondary p-button-sm segment-values-dialog__remove"
              :aria-label="t('segmentation.valuesField.remove')"
              @click="remove(item.value)"
            />
          </div>
        </template>
      </VirtualScroller>
    </div>
    <p
      v-else
      class="segment-values-dialog__empty"
    >
      {{ draft.length ? t('segmentation.valuesField.noResult') : t('segmentation.valuesField.empty') }}
    </p>
    <small
      v-if="search.trim() && draft.length"
      class="segment-values-dialog__message"
    >{{ t('segmentation.valuesField.matches', { count: filteredItems.length, total: draft.length }) }}</small>

    <template #footer>
      <div class="segment-values-dialog__footer">
        <Button
          type="button"
          :label="t('common.cancel')"
          class="p-button-text p-button-secondary"
          @click="close"
        />
        <Button
          type="button"
          :label="t('segmentation.valuesField.validate')"
          @click="validate"
        />
      </div>
    </template>
  </Dialog>
</template>

<script lang="ts">
import {
  computed, defineComponent, PropType, ref, watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import Dialog from 'primevue/dialog';
import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import Textarea from 'primevue/textarea';
import VirtualScroller from 'primevue/virtualscroller';

// Height of a row of the list, and rows shown before it scrolls
const ROW_HEIGHT = 40;
const MAX_ROWS = 8;

/**
 * Window to manage the values of a field holding many of them (hundreds of
 * URLs, codes…): add a value or a pasted list, search, remove, copy. It works
 * on a copy of the values: only « Valider » gives the new list back.
 */
export default defineComponent({
  name: 'SegmentValuesDialog',

  components: {
    Dialog,
    Button,
    InputText,
    Textarea,
    VirtualScroller,
  },

  props: {
    visible: {
      type: Boolean,
      required: true,
    },

    values: {
      type: Array as PropType<unknown[]>,
      required: true,
    },

    // Property holding the label when the values are objects
    field: {
      type: String,
      required: false,
      default: undefined,
    },

    title: {
      type: String,
      required: false,
      default: '',
    },
  },

  emits: ['update:visible', 'validate'],

  setup(props, { emit }) {
    const { t } = useI18n();
    const draft = ref<unknown[]>([]);
    const addText = ref('');
    const addMessage = ref<{ text: string; error: boolean } | null>(null);
    const search = ref('');
    const copied = ref(false);

    const labelOf = (value: unknown): string => {
      if (value !== null && typeof value === 'object' && props.field) {
        return String((value as Record<string, unknown>)[props.field] ?? '');
      }
      return String(value ?? '');
    };

    // Each opening starts from the values of the field
    watch(() => props.visible, (visible) => {
      if (!visible) {
        return;
      }
      draft.value = [...(props.values ?? [])];
      addText.value = '';
      addMessage.value = null;
      search.value = '';
      copied.value = false;
    }, { immediate: true });

    const countLabel = (count: number): string => {
      if (count === 0) {
        return t('segmentation.valuesField.countNone');
      }
      return count === 1
        ? t('segmentation.valuesField.countOne')
        : t('segmentation.valuesField.count', { count });
    };

    const filteredItems = computed(() => {
      const query = search.value.trim().toLowerCase();
      return draft.value
        .map((value) => ({ value, label: labelOf(value) }))
        .filter((item) => !query || item.label.toLowerCase().includes(query));
    });

    const listHeight = computed(() => `${Math.min(filteredItems.value.length, MAX_ROWS) * ROW_HEIGHT}px`);

    // One value per line, trimmed, blank lines dropped, values already listed kept once
    const addFromText = () => {
      const values = addText.value.split(/\r?\n/).map((value) => value.trim()).filter((value) => value !== '');
      if (!values.length) {
        addMessage.value = { text: t('segmentation.valuesField.addEmpty'), error: true };
        return;
      }

      const labels = new Set(draft.value.map(labelOf));
      const next = [...draft.value];
      let added = 0;
      let repeated = 0;
      values.forEach((value) => {
        if (labels.has(value)) {
          repeated += 1;
          return;
        }
        labels.add(value);
        next.push(value);
        added += 1;
      });

      draft.value = next;
      addText.value = '';
      const text = repeated
        ? t('segmentation.valuesField.addedWithRepeats', { added, repeated })
        : t('segmentation.valuesField.added', { added });
      addMessage.value = { text, error: false };
    };

    const remove = (value: unknown) => {
      draft.value = draft.value.filter((item) => item !== value);
    };

    const clearAll = () => {
      draft.value = [];
    };

    const copy = async () => {
      try {
        await navigator.clipboard.writeText(draft.value.map(labelOf).join('\n'));
        copied.value = true;
        setTimeout(() => {
          copied.value = false;
        }, 2000);
      } catch {
        addMessage.value = { text: t('segmentation.valuesField.copyFailed'), error: true };
      }
    };

    const close = () => {
      emit('update:visible', false);
    };

    const validate = () => {
      emit('validate', [...draft.value]);
      emit('update:visible', false);
    };

    return {
      t,
      draft,
      addText,
      addMessage,
      search,
      copied,
      rowHeight: ROW_HEIGHT,
      countLabel,
      filteredItems,
      listHeight,
      addFromText,
      remove,
      clearAll,
      copy,
      close,
      validate,
    };
  },
});
</script>

<style lang="scss">
// The theme centers every dialog content
.p-dialog.segment-values-dialog .p-dialog-content {
  text-align: left;
}

.segment-values-dialog {

  &__header {
    display: flex;
    align-items: baseline;
    gap: 0.75rem;
  }

  &__title {
    font-weight: 600;
  }

  &__count {
    font-size: 0.875rem;
    color: #6b7280;
  }

  &__add {
    display: flex;
    gap: 0.5rem;
    align-items: flex-start;
  }

  &__add-input {
    flex: 1;
    min-width: 0;
    resize: none;
  }

  &__message {
    display: block;
    margin-top: 0.35rem;
    font-size: 0.75rem;
    color: #6b7280;
  }

  &__toolbar {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    margin: 1rem 0 0.5rem;
  }

  &__search {
    flex: 1;
    min-width: 0;

    .p-inputtext {
      width: 100%;
    }
  }

  &__tool.p-button {
    flex-shrink: 0;
    white-space: nowrap;
  }

  &__list {
    border: 1px solid #e5e7eb;
    border-radius: 0.375rem;
    overflow: hidden;
  }

  &__row {
    box-sizing: border-box;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
    padding: 0 0.25rem 0 0.75rem;
    border-bottom: 1px solid #f3f4f6;
    text-align: left;

    &:hover {
      background: #f9fafb;
    }
  }

  &__value {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    text-align: left;
    font-size: 0.875rem;
    color: #374151;
  }

  &__remove.p-button {
    flex-shrink: 0;
    color: #9ca3af;

    &:hover {
      color: #dc2626;
    }
  }

  &__empty {
    margin: 0;
    padding: 1rem;
    text-align: center;
    color: #6b7280;
    border: 1px dashed #e5e7eb;
    border-radius: 0.375rem;
  }

  &__footer {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 0.5rem;

    .p-button {
      margin: 0;
    }
  }
}
</style>
